import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { acceptAll, readConsent, rejectAll, subscribe, writeConsent, type ConsentState } from "./consent-store";

interface ConsentContextValue {
  state: ConsentState;
  acceptAll: () => void;
  rejectAll: () => void;
  update: (partial: { analytics?: boolean; marketing?: boolean }) => void;
  /** Opens the preferences dialog regardless of whether a decision was already made. */
  openPreferences: () => void;
  /** Used by the preferences dialog to close itself. */
  closePreferences: () => void;
  preferencesOpen: boolean;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState>(() => readConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => subscribe(setState), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      acceptAll: () => {
        acceptAll();
        setPreferencesOpen(false);
      },
      rejectAll: () => {
        rejectAll();
        setPreferencesOpen(false);
      },
      update: (partial) => {
        writeConsent(partial);
        setPreferencesOpen(false);
      },
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
      preferencesOpen,
    }),
    [state, preferencesOpen],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}

/**
 * Lightweight global trigger for non-React callers (footer anchors, privacy policy links)
 * that want to open the preferences dialog. Dispatches a CustomEvent the provider listens to.
 */
export const OPEN_PREFERENCES_EVENT = "ezyhub:consent:open-preferences";

export function requestOpenPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}

export function useOpenPreferencesFromEvents() {
  const { openPreferences } = useConsent();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => openPreferences();
    window.addEventListener(OPEN_PREFERENCES_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handler);
  }, [openPreferences]);
}
